export class ApiError extends Error {
  status: number;

  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

const API_BASE = normalizeApiBase(import.meta.env.VITE_API_URL ?? '');

interface ApiFeedbackDetail {
  type: 'success' | 'error';
  message: string;
}

interface RequestFeedbackOptions {
  successMessage?: string;
  silentSuccess?: boolean;
}

function emitFeedback(detail: ApiFeedbackDetail): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new CustomEvent<ApiFeedbackDetail>('api-feedback', { detail }));
}

function isMutatingRequest(init: RequestInit): boolean {
  const method = (init.method ?? 'GET').toUpperCase();
  return method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE';
}

function inferSuccessMessage(data: unknown): string {
  if (
    data &&
    typeof data === 'object' &&
    'status' in data &&
    typeof data.status === 'string' &&
    data.status.length > 0
  ) {
    return data.status;
  }

  return 'Operation succeeded';
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  feedback: RequestFeedbackOptions = {},
): Promise<T> {
  const mutating = isMutatingRequest(init);
  const response = await fetch(buildApiUrl(path), {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.headers ?? {}),
    },
  });

  const data = (await response.json().catch(() => null)) as
    | T
    | {
        error?: string;
      }
    | null;

  if (!response.ok) {
    const message =
      data && typeof data === 'object' && 'error' in data && data.error
        ? data.error
        : `Request failed (${response.status})`;
    if (mutating) {
      emitFeedback({ type: 'error', message });
    }
    throw new ApiError(response.status, message);
  }

  if (mutating && !feedback.silentSuccess) {
    emitFeedback({
      type: 'success',
      message: feedback.successMessage ?? inferSuccessMessage(data),
    });
  }

  return data as T;
}

function normalizeApiBase(base: string): string {
  const trimmed = base.trim().replace(/\/+$/, '');
  return trimmed;
}

function buildApiUrl(path: string): string {
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;

  if (!API_BASE) {
    return normalizedPath;
  }

  const baseEndsWithApi = /\/api$/i.test(API_BASE);
  const pathStartsWithApi = /^\/api(\/|$)/i.test(normalizedPath);

  if (baseEndsWithApi && pathStartsWithApi) {
    return `${API_BASE}${normalizedPath.slice(4)}`;
  }

  return `${API_BASE}${normalizedPath}`;
}

export interface SessionUser {
  id: string;
  email: string;
  display_name: string;
}

export interface SessionResponse {
  user: SessionUser;
  expires_at?: string;
}

export function getSession(): Promise<SessionResponse> {
  return request<SessionResponse>('/api/auth/session');
}

export function loginWithPassword(payload: { email: string; password: string }): Promise<{
  user: SessionUser;
  has_passkey: boolean;
  should_register_passkey: boolean;
}> {
  return request('/api/auth/login', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function logout(): Promise<{ status: string }> {
  return request('/api/auth/logout', { method: 'POST' });
}

export function getPasskeyLoginOptions(): Promise<{ options: unknown; sessionToken: string }> {
  return request('/api/auth/passkey/login-options', { method: 'POST' });
}

export function verifyPasskeyLogin(payload: {
  sessionToken: string;
  response: unknown;
}): Promise<{ user: SessionUser }> {
  return request('/api/auth/passkey/login-verify', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function getPasskeyRegisterOptions(): Promise<{ options: unknown; sessionToken: string }> {
  return request('/api/auth/passkey/register-options', { method: 'POST' });
}

export function verifyPasskeyRegister(payload: {
  sessionToken: string;
  response: unknown;
  label?: string;
}): Promise<{ status: string; credential_id: string }> {
  return request('/api/auth/passkey/register-verify', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export interface AccountSession {
  id: string;
  login_method: string;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
  expires_at: string;
  current: boolean;
}

export function listAccountSessions(): Promise<AccountSession[]> {
  return request<AccountSession[]>('/api/account/sessions');
}

export function updatePassword(payload: {
  current_password?: string;
  new_password: string;
}): Promise<{ status: string }> {
  return request('/api/account/password', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function updateProfile(payload: { display_name: string }): Promise<{
  status: string;
  user: SessionUser;
}> {
  return request('/api/account/profile', {
    method: 'PUT',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
}

export function apiGet<T>(path: string): Promise<T> {
  return request<T>(path);
}

export function apiPost<T>(
  path: string,
  payload: unknown,
  feedback?: RequestFeedbackOptions,
): Promise<T> {
  return request<T>(
    path,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    },
    feedback,
  );
}

export function apiPut<T>(
  path: string,
  payload: unknown,
  feedback?: RequestFeedbackOptions,
): Promise<T> {
  return request<T>(
    path,
    {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    },
    feedback,
  );
}

export function apiPatch<T>(
  path: string,
  payload: unknown,
  feedback?: RequestFeedbackOptions,
): Promise<T> {
  return request<T>(
    path,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    },
    feedback,
  );
}

export function apiDelete<T>(path: string, feedback?: RequestFeedbackOptions): Promise<T> {
  return request<T>(path, { method: 'DELETE' }, feedback);
}
